import { IntlShape } from '@edx/frontend-platform/i18n';
import {
  AccessTimeFilled as AccessTimeFilledIcon,
  CalendarToday as CalendarTodayIcon,
  BusinessCenter as BusinessCenterIcon,
  Language as LanguageIcon,
  MenuBook as MenuBookIcon,
  Timeline as TimelineIcon,
} from '@openedx/paragon/icons';

import { formatDate } from '@src/utils';
import { SIDEBAR_DETAIL_KEYS } from './constants';
import type { CourseAboutData } from '../../types';
import messages from './messages';

const getLanguageName = (languageCode: string, locale: string) => {
  try {
    const languageNames = new Intl.DisplayNames([locale], { type: 'language' });
    return languageNames.of(languageCode) || languageCode;
  } catch {
    return languageCode;
  }
};

const getStartValue = (courseAboutData: CourseAboutData, intl: IntlShape) => {
  if (courseAboutData.advertisedStart) {
    const advertisedStartDate = new Date(courseAboutData.advertisedStart);
    if (!Number.isNaN(advertisedStartDate.getTime())) {
      return formatDate(courseAboutData.advertisedStart, intl);
    }

    return courseAboutData.advertisedStart;
  }

  if (courseAboutData.start) {
    return formatDate(courseAboutData.start, intl);
  }

  return '';
};

const getPacingLabel = (courseAboutData: CourseAboutData, intl: IntlShape) => {
  if (courseAboutData.pacing === 'self') {
    return intl.formatMessage(messages.selfPaced);
  }

  if (courseAboutData.pacing === 'instructor') {
    return intl.formatMessage(messages.instructorPaced);
  }

  return courseAboutData.pacing;
};

/**
 * Generates an array of sidebar detail objects for course information display.
 * Each detail object contains metadata about a specific course attribute.
*/
export const getSidebarDetails = (
  intl: IntlShape,
  courseAboutData: CourseAboutData,
) => [
  {
    key: SIDEBAR_DETAIL_KEYS.COURSE_NUMBER,
    icon: MenuBookIcon,
    label: intl.formatMessage(messages.courseNumber),
    value: courseAboutData.displayNumberWithDefault,
    show: true,
  },
  {
    key: SIDEBAR_DETAIL_KEYS.ORGANIZATION,
    icon: BusinessCenterIcon,
    label: intl.formatMessage(messages.organization),
    value: courseAboutData.displayOrgWithDefault,
    show: !!courseAboutData.displayOrgWithDefault,
  },
  {
    key: SIDEBAR_DETAIL_KEYS.START_DATE,
    icon: CalendarTodayIcon,
    label: intl.formatMessage(messages.classesStart),
    value: getStartValue(courseAboutData, intl),
    show: !courseAboutData.startDateIsStillDefault,
  },
  {
    key: SIDEBAR_DETAIL_KEYS.END_DATE,
    icon: CalendarTodayIcon,
    label: intl.formatMessage(messages.classesEnd),
    value: formatDate((courseAboutData.end ?? ''), intl),
    show: !!courseAboutData.end,
  },
  {
    key: SIDEBAR_DETAIL_KEYS.EFFORT,
    icon: AccessTimeFilledIcon,
    label: intl.formatMessage(messages.estimatedEffort),
    value: courseAboutData.effort,
    show: !!courseAboutData.effort,
  },
  {
    key: SIDEBAR_DETAIL_KEYS.LANGUAGE,
    icon: LanguageIcon,
    label: intl.formatMessage(messages.language),
    value: getLanguageName(courseAboutData.language, intl.locale),
    show: !!courseAboutData.language,
  },
  {
    key: SIDEBAR_DETAIL_KEYS.PACING,
    icon: TimelineIcon,
    label: intl.formatMessage(messages.pacing),
    value: getPacingLabel(courseAboutData, intl),
    show: !!courseAboutData.pacing,
  },
  {
    key: SIDEBAR_DETAIL_KEYS.REQUIREMENTS,
    icon: MenuBookIcon,
    label: intl.formatMessage(messages.requirements),
    value: courseAboutData.requirements,
    show: !!courseAboutData?.requirements,
  },
];
